import type { Gender, Lang, ParentSubtype, Relationship, UUID } from './types';

/**
 * Family graph built from approved structural relationships. Sibling,
 * grandparent, cousin and in-law relationships are derived here — never
 * stored (section 5.3 / 10.4).
 */
export interface FamilyGraph {
  parentsOf: Map<UUID, UUID[]>;
  childrenOf: Map<UUID, UUID[]>;
  partnersOf: Map<UUID, UUID[]>;
  /** relationship id lookup by pair key */
  parentEdge: Map<string, Relationship>;
  partnerEdge: Map<string, Relationship>;
  personIds: Set<UUID>;
}

export function pairKey(a: UUID, b: UUID): string {
  return `${a}→${b}`;
}

export function partnerKey(a: UUID, b: UUID): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

export function buildGraph(personIds: Iterable<UUID>, relationships: Relationship[], includeStatuses: Array<Relationship['status']> = ['approved']): FamilyGraph {
  const g: FamilyGraph = {
    parentsOf: new Map(),
    childrenOf: new Map(),
    partnersOf: new Map(),
    parentEdge: new Map(),
    partnerEdge: new Map(),
    personIds: new Set(personIds),
  };
  const push = (m: Map<UUID, UUID[]>, k: UUID, v: UUID) => {
    const arr = m.get(k);
    if (arr) {
      if (!arr.includes(v)) arr.push(v);
    } else m.set(k, [v]);
  };
  for (const r of relationships) {
    if (!includeStatuses.includes(r.status)) continue;
    if (!g.personIds.has(r.fromPersonId) || !g.personIds.has(r.toPersonId)) continue;
    if (r.type === 'parent_child') {
      push(g.parentsOf, r.toPersonId, r.fromPersonId);
      push(g.childrenOf, r.fromPersonId, r.toPersonId);
      g.parentEdge.set(pairKey(r.fromPersonId, r.toPersonId), r);
    } else {
      push(g.partnersOf, r.fromPersonId, r.toPersonId);
      push(g.partnersOf, r.toPersonId, r.fromPersonId);
      g.partnerEdge.set(partnerKey(r.fromPersonId, r.toPersonId), r);
    }
  }
  return g;
}

export const parents = (g: FamilyGraph, id: UUID): UUID[] => g.parentsOf.get(id) ?? [];
export const children = (g: FamilyGraph, id: UUID): UUID[] => g.childrenOf.get(id) ?? [];
export const partners = (g: FamilyGraph, id: UUID): UUID[] => g.partnersOf.get(id) ?? [];

export function siblings(g: FamilyGraph, id: UUID): { id: UUID; full: boolean }[] {
  const ps = parents(g, id);
  const seen = new Map<UUID, number>();
  for (const p of ps) for (const c of children(g, p)) if (c !== id) seen.set(c, (seen.get(c) ?? 0) + 1);
  return [...seen.entries()].map(([sid, shared]) => ({ id: sid, full: shared >= 2 && ps.length >= 2 }));
}

export function grandparents(g: FamilyGraph, id: UUID): UUID[] {
  const out = new Set<UUID>();
  for (const p of parents(g, id)) for (const gp of parents(g, p)) out.add(gp);
  return [...out];
}

export function grandchildren(g: FamilyGraph, id: UUID): UUID[] {
  const out = new Set<UUID>();
  for (const c of children(g, id)) for (const gc of children(g, c)) out.add(gc);
  return [...out];
}

export function ancestors(g: FamilyGraph, id: UUID, maxDepth = 50): Set<UUID> {
  const out = new Set<UUID>();
  let frontier = [id];
  for (let d = 0; d < maxDepth && frontier.length; d++) {
    const next: UUID[] = [];
    for (const f of frontier) for (const p of parents(g, f)) if (!out.has(p)) { out.add(p); next.push(p); }
    frontier = next;
  }
  return out;
}

export function descendants(g: FamilyGraph, id: UUID, maxDepth = 50): Set<UUID> {
  const out = new Set<UUID>();
  let frontier = [id];
  for (let d = 0; d < maxDepth && frontier.length; d++) {
    const next: UUID[] = [];
    for (const f of frontier) for (const c of children(g, f)) if (!out.has(c)) { out.add(c); next.push(c); }
    frontier = next;
  }
  return out;
}

/** Adding `parentId → childId` would create an ancestry cycle? (section 10.4) */
export function wouldCreateCycle(g: FamilyGraph, parentId: UUID, childId: UUID): boolean {
  if (parentId === childId) return true;
  return ancestors(g, parentId).has(childId);
}

/**
 * "My Branch" (section 9.3): the person, their parents, partners, siblings and
 * all descendants (plus descendants' partners so couples stay intact).
 */
export function branchOf(g: FamilyGraph, id: UUID): Set<UUID> {
  const out = new Set<UUID>([id]);
  for (const p of parents(g, id)) out.add(p);
  for (const p of partners(g, id)) out.add(p);
  for (const s of siblings(g, id)) out.add(s.id);
  for (const d of descendants(g, id)) {
    out.add(d);
    for (const p of partners(g, d)) out.add(p);
  }
  return out;
}

/** Connected component containing `id` (for isolating disconnected people). */
export function component(g: FamilyGraph, id: UUID): Set<UUID> {
  const out = new Set<UUID>([id]);
  const stack = [id];
  while (stack.length) {
    const cur = stack.pop()!;
    for (const n of [...parents(g, cur), ...children(g, cur), ...partners(g, cur)]) {
      if (!out.has(n)) { out.add(n); stack.push(n); }
    }
  }
  return out;
}

/**
 * Generation numbers: children are exactly one level below their parents,
 * partners share a level. Computed by monotone relaxation so the same data
 * always produces the same levels (section 9.1 / 9.2). Returns 0-based levels
 * where 0 is the oldest generation present.
 */
export function generations(g: FamilyGraph, ids: UUID[]): Map<UUID, number> {
  const gen = new Map<UUID, number>();
  const idSet = new Set(ids);
  for (const id of ids) gen.set(id, 0);
  const has = (x: UUID) => idSet.has(x);
  let changed = true;
  let guard = 0;
  while (changed && guard++ < ids.length + 5) {
    changed = false;
    for (const id of ids) {
      const g0 = gen.get(id)!;
      for (const p of parents(g, id)) {
        if (!has(p)) continue;
        const want = gen.get(p)! + 1;
        if (want > gen.get(id)!) { gen.set(id, want); changed = true; }
      }
      for (const s of partners(g, id)) {
        if (!has(s)) continue;
        const gs = gen.get(s)!;
        const me = gen.get(id)!;
        if (gs > me) { gen.set(id, gs); changed = true; } else if (me > gs) { gen.set(s, me); changed = true; }
      }
      void g0;
    }
  }
  // After partners are pulled down, their children may need to move down again — already covered by the loop.
  return gen;
}

// ---------------------------------------------------------------------------
// Kinship labels ("Your grandmother", "סבתא שלך") — plain words, not graph terms
// ---------------------------------------------------------------------------

export type KinKey =
  | 'self' | 'mother' | 'father' | 'parent' | 'daughter' | 'son' | 'child'
  | 'sister' | 'brother' | 'sibling' | 'half_sister' | 'half_brother' | 'half_sibling'
  | 'grandmother' | 'grandfather' | 'grandparent' | 'granddaughter' | 'grandson' | 'grandchild'
  | 'great_grandmother' | 'great_grandfather' | 'great_grandparent' | 'great_granddaughter' | 'great_grandson' | 'great_grandchild'
  | 'aunt' | 'uncle' | 'auntuncle' | 'niece' | 'nephew' | 'niecenephew' | 'cousin'
  | 'wife' | 'husband' | 'partner' | 'former_partner'
  | 'mother_in_law' | 'father_in_law' | 'parent_in_law' | 'daughter_in_law' | 'son_in_law' | 'child_in_law'
  | 'sister_in_law' | 'brother_in_law' | 'sibling_in_law'
  | 'stepmother' | 'stepfather' | 'stepparent' | 'stepdaughter' | 'stepson' | 'stepchild'
  | 'relative';

const KIN: Record<KinKey, { en: string; he: string }> = {
  self: { en: 'You', he: 'את/ה' },
  mother: { en: 'Your mother', he: 'אמא שלך' },
  father: { en: 'Your father', he: 'אבא שלך' },
  parent: { en: 'Your parent', he: 'ההורה שלך' },
  daughter: { en: 'Your daughter', he: 'הבת שלך' },
  son: { en: 'Your son', he: 'הבן שלך' },
  child: { en: 'Your child', he: 'הילד/ה שלך' },
  sister: { en: 'Your sister', he: 'אחותך' },
  brother: { en: 'Your brother', he: 'אחיך' },
  sibling: { en: 'Your sibling', he: 'אח/ות שלך' },
  half_sister: { en: 'Your half-sister', he: 'אחותך למחצה' },
  half_brother: { en: 'Your half-brother', he: 'אחיך למחצה' },
  half_sibling: { en: 'Your half-sibling', he: 'אח/ות למחצה' },
  grandmother: { en: 'Your grandmother', he: 'סבתא שלך' },
  grandfather: { en: 'Your grandfather', he: 'סבא שלך' },
  grandparent: { en: 'Your grandparent', he: 'סבא/סבתא שלך' },
  granddaughter: { en: 'Your granddaughter', he: 'נכדתך' },
  grandson: { en: 'Your grandson', he: 'נכדך' },
  grandchild: { en: 'Your grandchild', he: 'נכד/ה שלך' },
  great_grandmother: { en: 'Your great-grandmother', he: 'סבתא רבתא שלך' },
  great_grandfather: { en: 'Your great-grandfather', he: 'סבא רבא שלך' },
  great_grandparent: { en: 'Your great-grandparent', he: 'סבא רבא/סבתא רבתא שלך' },
  great_granddaughter: { en: 'Your great-granddaughter', he: 'נינתך' },
  great_grandson: { en: 'Your great-grandson', he: 'נינך' },
  great_grandchild: { en: 'Your great-grandchild', he: 'נין/ה שלך' },
  aunt: { en: 'Your aunt', he: 'דודה שלך' },
  uncle: { en: 'Your uncle', he: 'דוד שלך' },
  auntuncle: { en: 'Your aunt/uncle', he: 'דוד/ה שלך' },
  niece: { en: 'Your niece', he: 'אחייניתך' },
  nephew: { en: 'Your nephew', he: 'אחיינך' },
  niecenephew: { en: 'Your niece/nephew', he: 'אחיין/ית שלך' },
  cousin: { en: 'Your cousin', he: 'בן/בת דוד/ה שלך' },
  wife: { en: 'Your wife', he: 'אשתך' },
  husband: { en: 'Your husband', he: 'בעלך' },
  partner: { en: 'Your partner', he: 'בן/בת הזוג שלך' },
  former_partner: { en: 'Your former partner', he: 'בן/בת הזוג לשעבר' },
  mother_in_law: { en: 'Your mother-in-law', he: 'חמותך' },
  father_in_law: { en: 'Your father-in-law', he: 'חמיך' },
  parent_in_law: { en: 'Your parent-in-law', he: 'חם/חמות שלך' },
  daughter_in_law: { en: 'Your daughter-in-law', he: 'כלתך' },
  son_in_law: { en: 'Your son-in-law', he: 'חתנך' },
  child_in_law: { en: 'Your child-in-law', he: 'חתן/כלה שלך' },
  sister_in_law: { en: 'Your sister-in-law', he: 'גיסתך' },
  brother_in_law: { en: 'Your brother-in-law', he: 'גיסך' },
  sibling_in_law: { en: 'Your sibling-in-law', he: 'גיס/ה שלך' },
  stepmother: { en: 'Your stepmother', he: 'אמך החורגת' },
  stepfather: { en: 'Your stepfather', he: 'אביך החורג' },
  stepparent: { en: 'Your step-parent', he: 'ההורה החורג/ת שלך' },
  stepdaughter: { en: 'Your stepdaughter', he: 'בתך החורגת' },
  stepson: { en: 'Your stepson', he: 'בנך החורג' },
  stepchild: { en: 'Your stepchild', he: 'ילד/ה חורג/ת שלך' },
  relative: { en: 'Your relative', he: 'קרוב/ת משפחה שלך' },
};

export function kinLabel(key: KinKey, lang: Lang): string {
  return KIN[key][lang];
}

function gendered(gender: Gender, f: KinKey, m: KinKey, n: KinKey): KinKey {
  return gender === 'female' ? f : gender === 'male' ? m : n;
}

/**
 * Relationship of `other` to `me`, as a plain-language key. Explores up to
 * three structural steps; anything further is "relative".
 */
export function kinshipKey(g: FamilyGraph, me: UUID, other: UUID, genderOf: (id: UUID) => Gender, subtypeOf?: (parent: UUID, child: UUID) => ParentSubtype | undefined): KinKey | null {
  if (me === other) return 'self';
  const gnd = genderOf(other);
  const sub = (p: UUID, c: UUID) => subtypeOf?.(p, c);

  if (parents(g, me).includes(other)) {
    if (sub(other, me) === 'step') return gendered(gnd, 'stepmother', 'stepfather', 'stepparent');
    return gendered(gnd, 'mother', 'father', 'parent');
  }
  if (children(g, me).includes(other)) {
    if (sub(me, other) === 'step') return gendered(gnd, 'stepdaughter', 'stepson', 'stepchild');
    return gendered(gnd, 'daughter', 'son', 'child');
  }
  if (partners(g, me).includes(other)) {
    const rel = g.partnerEdge.get(partnerKey(me, other));
    if (rel && rel.current === false) return 'former_partner';
    if (rel?.subtype === 'married') return gendered(gnd, 'wife', 'husband', 'partner');
    return 'partner';
  }
  const sib = siblings(g, me).find((s) => s.id === other);
  if (sib) {
    return sib.full || parents(g, me).length < 2
      ? gendered(gnd, 'sister', 'brother', 'sibling')
      : gendered(gnd, 'half_sister', 'half_brother', 'half_sibling');
  }
  if (grandparents(g, me).includes(other)) return gendered(gnd, 'grandmother', 'grandfather', 'grandparent');
  if (grandchildren(g, me).includes(other)) return gendered(gnd, 'granddaughter', 'grandson', 'grandchild');
  // great-grandparents / great-grandchildren
  for (const gp of grandparents(g, me)) if (parents(g, gp).includes(other)) return gendered(gnd, 'great_grandmother', 'great_grandfather', 'great_grandparent');
  for (const gc of grandchildren(g, me)) if (children(g, gc).includes(other)) return gendered(gnd, 'great_granddaughter', 'great_grandson', 'great_grandchild');
  // aunts / uncles (parent's siblings and their partners)
  for (const p of parents(g, me)) {
    for (const s of siblings(g, p)) {
      if (s.id === other) return gendered(gnd, 'aunt', 'uncle', 'auntuncle');
      if (partners(g, s.id).includes(other)) return gendered(gnd, 'aunt', 'uncle', 'auntuncle');
    }
  }
  // nieces / nephews
  for (const s of siblings(g, me)) if (children(g, s.id).includes(other)) return gendered(gnd, 'niece', 'nephew', 'niecenephew');
  // cousins
  for (const p of parents(g, me)) for (const s of siblings(g, p)) if (children(g, s.id).includes(other)) return 'cousin';
  // in-laws
  for (const sp of partners(g, me)) {
    if (parents(g, sp).includes(other)) return gendered(gnd, 'mother_in_law', 'father_in_law', 'parent_in_law');
    if (siblings(g, sp).some((s) => s.id === other)) return gendered(gnd, 'sister_in_law', 'brother_in_law', 'sibling_in_law');
  }
  for (const c of children(g, me)) if (partners(g, c).includes(other)) return gendered(gnd, 'daughter_in_law', 'son_in_law', 'child_in_law');
  for (const s of siblings(g, me)) if (partners(g, s.id).includes(other)) return gendered(gnd, 'sister_in_law', 'brother_in_law', 'sibling_in_law');
  // anything connected
  if (component(g, me).has(other)) return 'relative';
  return null;
}

/** Structural validation shared by UI preview and repositories (section 10.4). */
export function validateNewRelationship(
  g: FamilyGraph,
  rel: { type: 'parent_child' | 'partner'; fromPersonId: UUID; toPersonId: UUID },
): { ok: true } | { ok: false; messageKey: 'rel.error.self' | 'rel.error.cycle' | 'rel.error.exists' | 'rel.error.tooManyParents' } {
  if (rel.fromPersonId === rel.toPersonId) return { ok: false, messageKey: 'rel.error.self' };
  if (rel.type === 'parent_child') {
    if (g.parentEdge.has(pairKey(rel.fromPersonId, rel.toPersonId))) return { ok: false, messageKey: 'rel.error.exists' };
    if (wouldCreateCycle(g, rel.fromPersonId, rel.toPersonId)) return { ok: false, messageKey: 'rel.error.cycle' };
    if (parents(g, rel.toPersonId).length >= 4) return { ok: false, messageKey: 'rel.error.tooManyParents' };
  } else if (g.partnerEdge.has(partnerKey(rel.fromPersonId, rel.toPersonId))) {
    return { ok: false, messageKey: 'rel.error.exists' };
  }
  return { ok: true };
}
